import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Typography,
  Grid,
  Stack,
  Button,
  Box,
  Alert,
  Card,
} from '@mui/material'
import { LoadingButton } from '@mui/lab'
import { Edit, XSquare } from 'react-feather'
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh'
import { ProtocolRoundInfoAccordion } from '@/features'
import {
  CardAttribute,
  LinkButton,
  ModalLayout,
  SharepointUrlLink,
  EntityPhoto,
  AuthorizationGuard,
  OriginalTextTooltip,
} from '@/components'
import { EnvironmentData } from './EnvironmentData'
import { VisitLogAction } from './VisitLogAction'
import { formatEnvironmentalData } from './formatEnvironmentalData'
import { ResearchEventDate } from '@/features/dataValidation/VisitLog/ResearchEventDate'
import {
  useModal,
  useMutationWrapper,
  usePutVisitLogStatus,
  useAiValidateFieldReport,
  useOtherRoundsPhotos,
} from '@/hooks'
import dayjs from 'dayjs'
import { getPath, getSpeciesWithResearchTypesString } from '@/utils'
import { UserGroups, VisitLog as VisitLogType } from '@/types'
import { DATE_FORMAT_DMY_TIME, ROUTES_PATH, PageReferrers } from '@/constants'
import {
  VisitLogFormContainer,
  ProcessingStatusForm,
  ProcessingStatusFormData,
} from '@/components/forms'
import { FieldReportFormContainer } from '@/components/forms/FieldReportFormContainer'
import { AutocompleteOption } from '@/components/inputs'
import {
  AttentionTaskForm,
  AttentionTaskFormData,
} from '@/components/forms/AttentionTaskForm'
import { AttentionTaskList } from '@/features/roundDetail/AttentionTaskList/AttentionTaskList'
import { useGetAttentionTasks } from '@/hooks/api/useGetAttentionTasks'
import { usePostAttentionTask } from '@/hooks/api/usePostAttentionTask'

export type DataValidationDetailsProps = {
  visitLog: VisitLogType
}

export const VisitLog = ({
  visitLog,
}: DataValidationDetailsProps): JSX.Element => {
  const { t } = useTranslation('dataValidation')
  const [data, setData] = useState<VisitLogType>(visitLog)

  const [isVisitLogEdit, setIsVisitLogEdit] = useState(false)
  const [isFieldReportEdit, setIsFieldReportEdit] = useState(false)

  const { otherRoundsHavePhotos } = useOtherRoundsPhotos({
    visitLog: data,
    enabled:
      !!data.research_visit_event?.field_report &&
      (!data.research_visit_event.field_report.photos ||
        data.research_visit_event.field_report.photos.length === 0),
  })

  const { close } = useModal()

  const { data: attentionTasks, isLoading: isLoadingAttentionTasks } =
    useGetAttentionTasks({
      target_id: data.id,
      target_type: 'research_visit_log',
      isEnabled: !!data?.id,
    })

  const {
    mutateAsync: createAttentionTask,
    isLoading: createAttentionTaskInProcess,
  } = useMutationWrapper(usePostAttentionTask)

  const handleAttentionTaskSubmit = async ({
    type,
    message,
  }: AttentionTaskFormData) => {
    await createAttentionTask(
      {
        type,
        message,
        target_type: 'research_visit_log',
        target_id: data.id,
      },
      {
        successMessageKey: 'monitoring:notifications.attentionTaskCreated',
      }
    )
  }

  const {
    mutateAsync: updateVisitLogStatus,
    isLoading: mutateStatusInProcess,
  } = useMutationWrapper(
    usePutVisitLogStatus.bind(null, {
      id: data.id,
    })
  )

  const handleStatusSubmit = async ({
    processingStatus,
    processingComment,
    isFieldReportAdded,
    assignedManager,
  }: ProcessingStatusFormData) => {
    const visitLogStatusData = {
      processing_status: !!processingStatus ? processingStatus : null,
      processing_comment: !!processingComment ? processingComment : null,
      is_field_report_added: isFieldReportAdded ?? false,
      assigned_manager_id: assignedManager?.value || null,
    }

    await updateVisitLogStatus(visitLogStatusData, {
      successMessageKey: 'dataValidation:notifications.processingStatusChanged',
      onSuccess: (data) => {
        setData(data)
      },
    })
  }

  const updatedDataHandler = (data: VisitLogType) => setData(data)

  const environmentalData = {
    start_temperature: data.start_temperature,
    end_temperature: data.end_temperature,
    urban_heat_temperature: data.urban_heat_temperature ?? null,
    cloud_cover: data.cloud_cover,
    wind_force: data.wind_force,
    wind_direction: data.wind_direction,
    precipitation: data.precipitation,
    precipitation_duration: data.precipitation_duration,
    fog_level: data.fog_level,
    fog_duration: data.fog_duration,
    invalid_variables: data.invalid_variables,
  }

  const protocol =
    data.research_visit_event?.research_area.research_area_protocols.find(
      (rap) => rap.protocol.id === data.research_visit_event?.protocol_id
    )?.protocol

  const handleEditVisitLog = () => {
    setIsVisitLogEdit(!isVisitLogEdit)
  }

  const handleEditFieldReport = () => {
    setIsFieldReportEdit(!isFieldReportEdit)
  }

  const { mutateAsync: aiValidateFieldReport, isLoading: isAiValidating } =
    useMutationWrapper(
      useAiValidateFieldReport.bind(null, {
        id: data.research_visit_event?.field_report?.id || 0,
      })
    )

  const hasBatloggerEquipment =
    data.research_visit_event?.research_area.project.required_equipment?.some(
      (equipment) => equipment.code === 'bat_logger'
    ) ?? false

  const handleAiValidate = async () => {
    if (data.research_visit_event?.field_report?.id) {
      try {
        await aiValidateFieldReport(undefined, {
          successMessageKey: 'dataValidation:notifications.fieldReportUpdated',
          onSuccess: (fieldReport) => {
            setData((prevData) => ({
              ...prevData,
              research_visit_event: prevData.research_visit_event
                ? {
                    ...prevData.research_visit_event,
                    field_report: fieldReport,
                  }
                : prevData.research_visit_event,
            }))
          },
        })
      } catch (error) {
        console.error('Error validating with AI:', error)
      }
    }
  }

  return (
    <ModalLayout
      title={t('visitDetailTitle', { id: data.id })}
      headerAction={
        <AuthorizationGuard
          allowedGroups={[UserGroups.MANAGER, UserGroups.DATA_CONTROLLER]}
          redirect={false}
        >
          <VisitLogAction visitLog={data} onDataUpdate={updatedDataHandler} />
        </AuthorizationGuard>
      }
    >
      <Grid container={true} rowSpacing={5} width={{ md: 925, sm: 'auto' }}>
        <>
          {!data ? (
            <Typography>{t('visit-log.noData')}</Typography>
          ) : (
            <>
              <Grid xs={12} item={true} container={true} spacing={4}>
                <Grid xs={3} item={true}>
                  <CardAttribute
                    title={t('visit-log.fieldTitles.projectNumber')}
                    value={
                      data.research_visit_event?.research_area.project
                        .project_number
                    }
                  />
                </Grid>
                <Grid xs={6} item={true}>
                  <CardAttribute
                    title={t('visit-log.fieldTitles.projectName')}
                    value={
                      data.research_visit_event?.research_area.project.name
                    }
                  />
                </Grid>
                <Grid xs={3} item={true}>
                  <CardAttribute
                    title={t('visit-log.fieldTitles.clientName')}
                    value={
                      data.research_visit_event?.research_area.project
                        .client_name
                    }
                  />
                </Grid>

                <Grid xs={3} item={true}>
                  <CardAttribute
                    title={t('visit-log.fieldTitles.researchAreaNumber')}
                    value={data.research_visit_event?.research_area.id}
                  />
                </Grid>
                <Grid xs={6} item={true}>
                  <CardAttribute
                    title={t('visit-log.fieldTitles.researchAreaLocation')}
                    value={
                      data.research_visit_event?.research_area.location_name
                    }
                  />
                </Grid>
                <Grid xs={3} item={true}>
                  <CardAttribute
                    title={t('visit-log.fieldTitles.client_area_number')}
                    value={
                      !!data.research_visit_event?.research_area
                        .client_area_number
                        ? data.research_visit_event?.research_area
                            .client_area_number
                        : '-'
                    }
                  />
                </Grid>
                <AuthorizationGuard
                  allowedGroups={[
                    UserGroups.MANAGER,
                    UserGroups.EMPLOYEE,
                    UserGroups.DATA_CONTROLLER,
                  ]}
                  redirect={false}
                >
                  {data.research_visit_event?.research_area
                    .report_sharepoint_url && (
                    <Grid xs={6} item={true}>
                      <CardAttribute
                        title={t('visit-log.fieldTitles.field_report_url')}
                        value={
                          <SharepointUrlLink
                            sharepointUrl={
                              data.research_visit_event?.research_area
                                .report_sharepoint_url
                            }
                          />
                        }
                      />
                    </Grid>
                  )}
                </AuthorizationGuard>
              </Grid>
              {protocol && (
                <Grid xs={12} item={true}>
                  <ProtocolRoundInfoAccordion
                    title={t('visit-log.fieldTitles.protocolInfo')}
                    allRounds={protocol.rounds}
                    round={protocol.rounds.find((r) =>
                      visitLog.research_visit_event?.visit_protocol_id
                        ? r.id ===
                          visitLog.research_visit_event.visit_protocol_id
                        : r.round_type ===
                          visitLog.research_visit_event?.round_type
                    )}
                  />
                </Grid>
              )}
              <Grid xs={12} item={true} container={true} spacing={4}>
                <Grid xs={3} item={true}>
                  <CardAttribute
                    title={t('visit-log.fieldTitles.round')}
                    value={
                      !!data.research_visit_event ? (
                        <AuthorizationGuard
                          allowedGroups={[UserGroups.MANAGER]}
                          redirect={false}
                          fallback={
                            <>{data.research_visit_event?.round_name ?? '-'}</>
                          }
                        >
                          <LinkButton
                            url={getPath(ROUTES_PATH.MONITORING_DETAIL, {
                              id: data.research_visit_event?.id,
                            })}
                            title={`${data.research_visit_event?.round_name} (${data.research_visit_event?.id})`}
                            referrer={PageReferrers.DATA_VALIDATION}
                            arrowIcon={true}
                            variant="textThin"
                            action={() => close()}
                          />
                        </AuthorizationGuard>
                      ) : (
                        '-'
                      )
                    }
                  />
                </Grid>
                <Grid xs={9} item={true}>
                  <CardAttribute
                    title={t('visit-log.fieldTitles.species')}
                    value={
                      !!data.research_visit_event
                        ? getSpeciesWithResearchTypesString(
                            data.research_visit_event
                          )
                        : '-'
                    }
                  />
                </Grid>
              </Grid>
              <Grid xs={12} item={true} container={true} spacing={4}>
                <Grid xs={3} item={true}>
                  <CardAttribute
                    title={t('visit-log.fieldTitles.creationDate')}
                    value={
                      data.created_at
                        ? dayjs(data.created_at)
                            .format(DATE_FORMAT_DMY_TIME)
                            .toString()
                        : '-'
                    }
                  />
                </Grid>
                <Grid xs={3} item={true}>
                  <CardAttribute
                    title={t('visit-log.fieldTitles.createdBy')}
                    value={`${data.created_by?.first_name ?? '-'} ${
                      data.created_by?.last_name ?? ''
                    }`}
                  />
                </Grid>
                <Grid xs={6} item={true}>
                  <CardAttribute
                    title={t('visit-log.fieldTitles.researchers')}
                    value={
                      data.researchers?.map(
                        ({ first_name, last_name }, i) =>
                          `${i > 0 ? ', ' : ''}${first_name} ${last_name}`
                      ) ?? t('visit-log.noResearchers')
                    }
                  />
                </Grid>
                {data.updated_at !== data.created_at && (
                  <>
                    <Grid xs={3} item={true}>
                      <CardAttribute
                        title="Edited on:"
                        value={
                          data.updated_at
                            ? dayjs(data.updated_at)
                                .format(DATE_FORMAT_DMY_TIME)
                                .toString()
                            : '-'
                        }
                      />
                    </Grid>
                    <Grid xs={9} item={true}>
                      <CardAttribute
                        title="Edited by:"
                        value={`${data.edited_by?.first_name ?? '-'} ${
                          data.edited_by?.last_name ?? ''
                        }`}
                      />
                    </Grid>
                  </>
                )}
              </Grid>

              <Grid xs={12} item={true}>
                <CardAttribute
                  title={t('visit-log.fieldTitles.observationLogs')}
                  value={
                    data.research_visit_event ? (
                      <LinkButton
                        url={`/observation-logs?research_visit_event=${data.research_visit_event.id}`}
                        title={t('visit-log.buttons.openObservationLogs')}
                        arrowIcon={true}
                        variant="textThin"
                        newTab={true}
                      />
                    ) : (
                      '-'
                    )
                  }
                />
              </Grid>

              <AuthorizationGuard
                allowedGroups={[
                  UserGroups.MANAGER,
                  UserGroups.EMPLOYEE,
                  UserGroups.DATA_CONTROLLER,
                ]}
                redirect={false}
              >
                <Stack
                  direction="row"
                  justifyContent="flex-end"
                  spacing={4}
                  width="100%"
                  mt={6}
                >
                  {data.research_visit_event && (
                    <Button
                      onClick={handleEditVisitLog}
                      variant="textThin"
                      endIcon={
                        isVisitLogEdit ? (
                          <XSquare size={14} />
                        ) : (
                          <Edit size={14} />
                        )
                      }
                    >
                      {isVisitLogEdit
                        ? t('buttons.closeEditing')
                        : t('buttons.editVisitLog')}
                    </Button>
                  )}
                </Stack>
              </AuthorizationGuard>

              {data.research_visit_event && isVisitLogEdit ? (
                <VisitLogFormContainer
                  data={data}
                  setUpdatedVisitLog={setData}
                  onSuccessUpdate={() => setIsVisitLogEdit(false)}
                />
              ) : (
                <Grid xs={12} item={true} container={true}>
                  <ResearchEventDate
                    start_datetime={data.start_datetime}
                    end_datetime={data.end_datetime}
                    invalid_variables={data.invalid_variables}
                  />

                  <CardAttribute
                    title={t('visit-log.sections.environmentalData')}
                    value={
                      <EnvironmentData
                        {...formatEnvironmentalData(environmentalData)}
                      />
                    }
                    mt={4}
                  />

                  {data.is_left_earlier && (
                    <Grid xs={6} item={true}>
                      <CardAttribute
                        title={t('visit-log.fieldTitles.leftEarlier')}
                        value={
                          <Typography
                            sx={
                              data.invalid_variables?.includes(
                                'is_left_earlier'
                              )
                                ? { color: 'error.main' }
                                : {}
                            }
                          >
                            {data.reason_of_leaving || '-'}
                          </Typography>
                        }
                        mt={4}
                      />
                    </Grid>
                  )}
                  {!!data.comment && (
                    <Grid xs={6} item={true}>
                      <CardAttribute
                        title={t('visit-log.fieldTitles.comment')}
                        value={data.comment}
                        mt={4}
                      />
                    </Grid>
                  )}
                  {hasBatloggerEquipment && (
                    <>
                      <Grid xs={6} item={true}>
                        <CardAttribute
                          title={t(
                            'visit-log.fieldTitles.batloggerDataUploaded'
                          )}
                          value={
                            data.is_batlogger_data_uploaded
                              ? t('visit-log.yes')
                              : t('visit-log.no')
                          }
                          mt={4}
                        />
                      </Grid>
                      <Grid xs={6} item={true}>
                        <CardAttribute
                          title={t('visit-log.fieldTitles.batloggerDataLost')}
                          value={
                            data.is_batlogger_data_lost
                              ? t('visit-log.yes')
                              : t('visit-log.no')
                          }
                          mt={4}
                        />
                      </Grid>
                    </>
                  )}
                  {!!data.photos.length && (
                    <Grid
                      xs={12}
                      item={true}
                      container={true}
                      spacing={4}
                      mt={2}
                    >
                      {data.photos.map((photo) => (
                        <EntityPhoto photo={photo} key={photo.id} />
                      ))}
                    </Grid>
                  )}
                </Grid>
              )}

              <AuthorizationGuard
                allowedGroups={[
                  UserGroups.MANAGER,
                  UserGroups.EMPLOYEE,
                  UserGroups.DATA_CONTROLLER,
                ]}
                redirect={false}
              >
                <Stack
                  direction="row"
                  justifyContent="flex-end"
                  width="100%"
                  mt={6}
                >
                  {data.research_visit_event &&
                    data.research_visit_event.field_report && (
                      <Button
                        onClick={handleEditFieldReport}
                        variant="textThin"
                        endIcon={
                          isFieldReportEdit ? (
                            <XSquare size={14} />
                          ) : (
                            <Edit size={14} />
                          )
                        }
                      >
                        {isFieldReportEdit
                          ? t('buttons.closeEditing')
                          : t('buttons.editFieldReport')}
                      </Button>
                    )}
                </Stack>
              </AuthorizationGuard>
              {data.research_visit_event &&
              data.research_visit_event.field_report &&
              isFieldReportEdit ? (
                <FieldReportFormContainer
                  data={data}
                  fieldReportId={data.research_visit_event.field_report.id}
                  setUpdatedVisitLog={setData}
                  onSuccessUpdate={() => setIsFieldReportEdit(false)}
                />
              ) : (
                <>
                  {data.research_visit_event?.field_report ? (
                    data.research_visit_event.field_report.auto_corrected ? (
                      <Grid xs={12} item={true}>
                        <Stack
                          direction="row"
                          spacing={1}
                          alignItems="center"
                          sx={{
                            backgroundColor: 'background.paper',
                            p: 1,
                          }}
                        >
                          <AutoFixHighIcon color="secondary" fontSize="small" />
                          <Typography color="text.primary" fontStyle="italic">
                            {t(
                              'dataValidation:visit-log.auto-corrected-notice',
                              'This text has been rewritten by AI'
                            )}
                          </Typography>
                        </Stack>
                      </Grid>
                    ) : (
                      <Grid xs={12} item={true}>
                        <LoadingButton
                          variant="contained"
                          color="secondary"
                          startIcon={<AutoFixHighIcon />}
                          onClick={handleAiValidate}
                          loading={isAiValidating}
                          sx={{ mb: 2 }}
                        >
                          {t(
                            'dataValidation:visit-log.ai-validate-button',
                            'Rewrite and validate with AI'
                          )}
                        </LoadingButton>
                      </Grid>
                    )
                  ) : null}
                  {!!data.research_visit_event?.field_report
                    ?.field_impression && (
                    <Grid xs={12} item={true}>
                      <Box position="relative">
                        <CardAttribute
                          title={t('visit-log.fieldTitles.fieldImpression')}
                          value={
                            data.research_visit_event?.field_report
                              ?.field_impression
                          }
                        />
                        {data.research_visit_event?.field_report
                          ?.auto_corrected &&
                          data.research_visit_event?.field_report
                            ?.original_field_impression && (
                            <OriginalTextTooltip
                              originalText={
                                data.research_visit_event.field_report
                                  .original_field_impression
                              }
                              iconSx={{
                                position: 'absolute',
                                top: 0,
                                right: 0,
                              }}
                            />
                          )}
                        {data.research_visit_event?.field_report
                          ?.field_impression_is_valid === false &&
                          data.research_visit_event?.field_report
                            ?.field_impression_validation_error && (
                            <Alert
                              severity="error"
                              sx={{
                                backgroundColor: 'background.paper',
                                p: 1,
                              }}
                            >
                              {
                                data.research_visit_event.field_report
                                  .field_impression_validation_error
                              }
                            </Alert>
                          )}
                      </Box>
                    </Grid>
                  )}
                  {!!data.research_visit_event?.field_report
                    ?.round_impression && (
                    <Grid xs={12} item={true}>
                      <Box position="relative">
                        <CardAttribute
                          title={t('visit-log.fieldTitles.roundImpression')}
                          value={
                            data.research_visit_event?.field_report
                              ?.round_impression
                          }
                        />
                        {data.research_visit_event?.field_report
                          ?.auto_corrected &&
                          data.research_visit_event?.field_report
                            ?.original_round_impression && (
                            <OriginalTextTooltip
                              originalText={
                                data.research_visit_event.field_report
                                  .original_round_impression
                              }
                              iconSx={{
                                position: 'absolute',
                                top: 0,
                                right: 0,
                              }}
                            />
                          )}
                        {data.research_visit_event?.field_report
                          ?.round_impression_is_valid === false &&
                          data.research_visit_event?.field_report
                            ?.round_impression_validation_error && (
                            <Alert
                              severity="error"
                              sx={{
                                backgroundColor: 'background.paper',
                                p: 1,
                              }}
                            >
                              {
                                data.research_visit_event.field_report
                                  .round_impression_validation_error
                              }
                            </Alert>
                          )}
                      </Box>
                    </Grid>
                  )}
                  {!!data.research_visit_event?.field_report?.photos?.length ? (
                    <Grid xs={12} item={true} container={true} spacing={4}>
                      {data.research_visit_event.field_report.photos.map(
                        (photo) => (
                          <EntityPhoto photo={photo} key={photo.id} />
                        )
                      )}
                    </Grid>
                  ) : (
                    // Display disclaimer when current field report has no photos but other rounds have photos
                    otherRoundsHavePhotos && (
                      <Grid xs={12} item={true}>
                        <Alert severity="info" sx={{ mt: 2 }}>
                          {t(
                            'dataValidation:visit-log.photos-in-other-rounds',
                            'No photos in this field report. Photos are uploaded to previous rounds.'
                          )}
                        </Alert>
                      </Grid>
                    )
                  )}
                </>
              )}
              <AuthorizationGuard
                allowedGroups={[UserGroups.MANAGER, UserGroups.DATA_CONTROLLER]}
                redirect={false}
              >
                <Grid item={true} sm={12}>
                  <Card
                    elevation={3}
                    sx={{
                      borderRadius: 2,
                      overflow: 'visible',
                      p: 3,
                      border: '1px solid rgba(0, 0, 0, 0.12)',
                    }}
                  >
                    <Typography variant="subtitle1" fontWeight="medium" mb={2}>
                      {t('monitoring:titles.attentionTasks')}
                    </Typography>
                    <Box>
                      <AttentionTaskForm
                        onSubmit={handleAttentionTaskSubmit}
                        defaultValues={{ type: 'photo_task', message: '' }}
                        isLoading={createAttentionTaskInProcess}
                      />
                      <Box mt={4}>
                        {isLoadingAttentionTasks ? (
                          <Typography variant="body2" color="text.secondary">
                            {t('monitoring:loading')}
                          </Typography>
                        ) : (
                          <AttentionTaskList
                            tasks={attentionTasks || []}
                            targetType="research_visit_log"
                            targetId={data.id}
                          />
                        )}
                      </Box>
                    </Box>
                  </Card>
                </Grid>
                <Grid item={true} sm={12}>
                  <Card
                    elevation={3}
                    sx={{
                      borderRadius: 2,
                      overflow: 'visible',
                      p: 3,
                      border: '1px solid rgba(0, 0, 0, 0.12)',
                    }}
                  >
                    <Typography variant="subtitle1" fontWeight="medium" mb={2}>
                      {t('visit-log.fieldTitles.processingStatus')}
                    </Typography>
                    <ProcessingStatusForm
                      statusSelect={true}
                      fieldReportCheckbox={true}
                      defaultValues={{
                        processingStatus: data.processing_status ?? '',
                        processingComment: data.processing_comment ?? '',
                        isFieldReportAdded: data.is_field_report_added ?? false,
                        assignedManager:
                          data && data.assigned_manager
                            ? ({
                                title: `${data.assigned_manager.first_name} ${data.assigned_manager.last_name} [${data.assigned_manager.email}]`,
                                value: data.assigned_manager.id,
                              } as AutocompleteOption<number>)
                            : null,
                      }}
                      onSubmit={handleStatusSubmit}
                      isLoading={mutateStatusInProcess}
                      processingStatusUpdatedAt={
                        data.processing_status_updated_at
                      }
                    />
                  </Card>
                </Grid>
              </AuthorizationGuard>
            </>
          )}
        </>
      </Grid>
    </ModalLayout>
  )
}
